import { Spinner } from "@/components/ui/Spinner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Edit,
  GripVertical,
  Image,
  MoveVertical,
  Package2,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/Dialog";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "../../components/ui/Dropzone";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useCategory } from "../../hooks/useCategory";
import { useCloudflareUpload } from "../../hooks/useCloudflareUpload";
import { Category } from "../../prisma-types";

// simple URL-friendly slug generator
function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

interface Subcategory {
  id: number;
  categoryId: number;
  name: string;
  slug: string;
  description?: string | null;
  displayOrder?: number | null;
}

export const ProductCategoryPannel = () => {
  const queryClient = useQueryClient();
  const [categorySearch, setCategorySearch] = React.useState("");
  const [isCreatingCategory, setIsCreatingCategory] = React.useState(false);
  const [editingCategory, setEditingCategory] = React.useState<Category | null>(
    null
  );
  const [deletingCategoryId, setDeletingCategoryId] = React.useState<
    number | null
  >(null);
  const [categoryFormData, setCategoryFormData] = React.useState({
    name: "",
    slug: "",
    imageUrl: "",
    cardImage: "",
  });
  const [categorySlugTouched, setCategorySlugTouched] = React.useState(false);

  // Image upload for category
  const categoryImageUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // Counter to force dropzone re-render when image is deleted
  const [dropzoneKey, setDropzoneKey] = React.useState(0);
  const [cardDropzoneKey, setCardDropzoneKey] = React.useState(0);

  // Track last uploadedResults length to detect new uploads
  const lastUploadedResultsLengthRef = React.useRef(0);
  const lastCardUploadedResultsLengthRef = React.useRef(0);

  // Image upload for category card image
  const categoryCardImageUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  const {
    categories,
    isLoading: categoriesLoading,
    refetch,
  } = useCategory({ fetchOnMount: true });

  const [reorderOpen, setReorderOpen] = React.useState(false);
  const [localCategoryOrder, setLocalCategoryOrder] = React.useState<
    Category[]
  >([]);
  React.useEffect(() => {
    if (categories && Array.isArray(categories)) {
      setLocalCategoryOrder(categories);
    }
  }, [categories]);

  const moveCategory = (fromIndex: number, toIndex: number) => {
    setLocalCategoryOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const reorderCategories = useMutation({
    mutationFn: async (orderIds: number[]) => {
      const response = await fetchWithAuth(`/api/admin/categories/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: orderIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la reordonarea categoriilor");
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success("Ordinea categoriilor a fost actualizată");
      setReorderOpen(false);
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      refetch();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const createCategory = useMutation({
    mutationFn: async (data: typeof categoryFormData) => {
      const response = await fetchWithAuth("/api/admin/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          slug: data.slug,
          imageUrl: data.imageUrl || null,
          cardImage: data.cardImage || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la crearea categoriei");
      }
      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      refetch();
      toast.success("Categorie creată cu succes");
      setIsCreatingCategory(false);
      setCategoryFormData({ name: "", slug: "", imageUrl: "", cardImage: "" });
      categoryImageUpload.setFiles([]);
      categoryImageUpload.setErrors([]);
      categoryCardImageUpload.setFiles([]);
      categoryCardImageUpload.setErrors([]);
      setDropzoneKey(0);
      setCardDropzoneKey(0);
      lastUploadedResultsLengthRef.current = 0;
      lastCardUploadedResultsLengthRef.current = 0;
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<typeof categoryFormData>;
    }) => {
      const response = await fetchWithAuth(`/api/admin/categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          slug: data.slug,
          imageUrl:
            data.imageUrl !== undefined ? data.imageUrl || null : undefined,
          cardImage:
            data.cardImage !== undefined ? data.cardImage || null : undefined,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Eroare la actualizarea categoriei"
        );
      }
      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      refetch();
      toast.success("Categorie actualizată cu succes");
      setEditingCategory(null);
      setCategoryFormData({ name: "", slug: "", imageUrl: "", cardImage: "" });
      categoryImageUpload.setFiles([]);
      categoryImageUpload.setErrors([]);
      categoryCardImageUpload.setFiles([]);
      categoryCardImageUpload.setErrors([]);
      setDropzoneKey(0);
      setCardDropzoneKey(0);
      lastUploadedResultsLengthRef.current = 0;
      lastCardUploadedResultsLengthRef.current = 0;
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/categories/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la ștergerea categoriei");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie ștearsă cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    onSettled: () => {
      setDeletingCategoryId(null);
    },
  });

  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      updateCategory.mutate({
        id: editingCategory.id,
        data: categoryFormData,
      });
    } else {
      createCategory.mutate(categoryFormData);
    }
  };

  const handleCategoryEdit = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      slug: category.slug,
      imageUrl: category.imageUrl || "",
      cardImage: (category as any).cardImage || "",
    });
    setIsCreatingCategory(true);
    // Reset ref when editing a category to track new uploads
    lastUploadedResultsLengthRef.current = 0;
  };

  const handleCategoryDelete = (id: number) => {
    if (
      window.confirm(
        "Sigur doriți să ștergeți această categorie? Această acțiune nu poate fi anulată."
      )
    ) {
      setDeletingCategoryId(id);
      deleteCategory.mutate(id);
    }
  };

  const handleCancelCategoryForm = () => {
    setIsCreatingCategory(false);
    setEditingCategory(null);
    setCategoryFormData({ name: "", slug: "", imageUrl: "", cardImage: "" });
    categoryImageUpload.setFiles([]);
    categoryImageUpload.setErrors([]);
    categoryCardImageUpload.setFiles([]);
    categoryCardImageUpload.setErrors([]);
    setDropzoneKey(0);
    setCardDropzoneKey(0);
    lastUploadedResultsLengthRef.current = 0;
    lastCardUploadedResultsLengthRef.current = 0;
  };

  // Reset uploader state when opening form for new category
  React.useEffect(() => {
    if (isCreatingCategory && !editingCategory) {
      // Reset all uploader state when opening form for a new category
      categoryImageUpload.setFiles([]);
      categoryImageUpload.setErrors([]);
      categoryCardImageUpload.setFiles([]);
      categoryCardImageUpload.setErrors([]);
      // Clear any uploaded results by resetting the form data imageUrl
      setCategoryFormData((prev) => ({ ...prev, imageUrl: "", cardImage: "" }));
      // Reset ref to track new uploads
      lastUploadedResultsLengthRef.current = 0;
      lastCardUploadedResultsLengthRef.current = 0;
    }
  }, [isCreatingCategory, editingCategory]);

  // Handle image upload completion - use only NEW uploaded results
  React.useEffect(() => {
    const currentLength = categoryImageUpload.uploadedResults.length;
    const lastLength = lastUploadedResultsLengthRef.current;

    // Only process if there are new results (length increased)
    if (
      categoryImageUpload.isSuccess &&
      currentLength > 0 &&
      currentLength > lastLength
    ) {
      // Get the last uploaded result (most recent)
      const latestResult =
        categoryImageUpload.uploadedResults[currentLength - 1];
      if (latestResult && latestResult.publicUrl) {
        setCategoryFormData((prev) => ({
          ...prev,
          imageUrl: latestResult.publicUrl,
        }));
        toast.success("Imagine încărcată cu succes");
        // Update ref to current length
        lastUploadedResultsLengthRef.current = currentLength;
      }
    } else if (currentLength !== lastLength) {
      // Update ref even if we don't process (to keep in sync)
      lastUploadedResultsLengthRef.current = currentLength;
    }
  }, [
    categoryImageUpload.isSuccess,
    categoryImageUpload.uploadedResults.length,
  ]);

  // Handle card image upload completion - use only NEW uploaded results
  React.useEffect(() => {
    const currentLength = categoryCardImageUpload.uploadedResults.length;
    const lastLength = lastCardUploadedResultsLengthRef.current;

    if (
      categoryCardImageUpload.isSuccess &&
      currentLength > 0 &&
      currentLength > lastLength
    ) {
      const latestResult =
        categoryCardImageUpload.uploadedResults[currentLength - 1];
      if (latestResult && latestResult.publicUrl) {
        setCategoryFormData((prev) => ({
          ...prev,
          cardImage: latestResult.publicUrl,
        }));
        toast.success("Imagine card încărcată cu succes");
        lastCardUploadedResultsLengthRef.current = currentLength;
      }
    } else if (currentLength !== lastLength) {
      lastCardUploadedResultsLengthRef.current = currentLength;
    }
  }, [
    categoryCardImageUpload.isSuccess,
    categoryCardImageUpload.uploadedResults.length,
  ]);

  const filteredCategories = React.useMemo(() => {
    if (!categories || !Array.isArray(categories)) {
      return [] as Category[];
    }
    return categories.filter((category) =>
      category.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  }, [categories, categorySearch]);

  const [expandedCategoryIds, setExpandedCategoryIds] = React.useState<
    Set<number>
  >(new Set());
  const toggleExpanded = (id: number) => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Card className="">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package2 className="w-5 h-5 text-purple-600" />
            Categorii Produse
          </div>
          <div className="flex gap-2">
            <Dialog open={reorderOpen} onOpenChange={setReorderOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <MoveVertical className="w-4 h-4" />
                  Reordonează
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reordonare categorii</DialogTitle>
                  <DialogDescription>
                    Mută categoriile sus/jos pentru a schimba ordinea.
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-auto space-y-2">
                  {localCategoryOrder.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      Nu există categorii
                    </div>
                  ) : (
                    localCategoryOrder.map((cat, idx) => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between rounded border p-2 "
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-foreground">
                            {cat.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              idx > 0 && moveCategory(idx, idx - 1)
                            }
                            disabled={idx === 0}
                          >
                            Sus
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              idx < localCategoryOrder.length - 1 &&
                              moveCategory(idx, idx + 1)
                            }
                            disabled={idx === localCategoryOrder.length - 1}
                          >
                            Jos
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={() => setReorderOpen(false)}>
                    Anulează
                  </Button>
                  <Button
                    onClick={() =>
                      reorderCategories.mutate(
                        localCategoryOrder.map((c) => c.id)
                      )
                    }
                    disabled={reorderCategories.isPending}
                  >
                    {reorderCategories.isPending
                      ? "Se salvează..."
                      : "Salvează ordinea"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button
              className="flex items-center gap-2 "
              onClick={() => setIsCreatingCategory(true)}
            >
              <Plus className="w-4 h-4" />
              Adaugă Categorie
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Category Form */}
        {isCreatingCategory && (
          <Card className="">
            <CardHeader>
              <CardTitle className="text-lg">
                {editingCategory
                  ? "Editează Categorie"
                  : "Adaugă Categorie Nouă"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Nume Categorie
                    </label>
                    <Input
                      value={categoryFormData.name}
                      onChange={(e) => {
                        const nextName = e.target.value;
                        setCategoryFormData((f) => ({
                          ...f,
                          name: nextName,
                          slug: categorySlugTouched
                            ? f.slug
                            : slugify(nextName),
                        }));
                      }}
                      placeholder="ex: Spiritualitate"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Slug
                    </label>
                    <Input
                      value={categoryFormData.slug}
                      onChange={(e) => {
                        setCategorySlugTouched(true);
                        const next = slugify(e.target.value);
                        setCategoryFormData((f) => ({ ...f, slug: next }));
                      }}
                      placeholder="ex: spiritualitate"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Se va salva automat lowercase pe server
                    </p>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Imagine Categorie
                  </label>
                  {categoryFormData.imageUrl ? (
                    <div className="relative">
                      <img
                        src={categoryFormData.imageUrl}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setCategoryFormData((f) => ({ ...f, imageUrl: "" }));
                          categoryImageUpload.setFiles([]);
                          categoryImageUpload.setErrors([]);
                          // Force re-render of dropzone by updating key
                          setDropzoneKey((prev) => prev + 1);
                          // Reset ref to track new uploads
                          lastUploadedResultsLengthRef.current = 0;
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Dropzone
                      {...categoryImageUpload}
                      key={`category-upload-${
                        editingCategory?.id || "new"
                      }-${dropzoneKey}`}
                    >
                      <DropzoneEmptyState />
                      <DropzoneContent />
                    </Dropzone>
                  )}
                  {categoryImageUpload.files.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        onClick={async () => {
                          await categoryImageUpload.onUpload();
                        }}
                        disabled={categoryImageUpload.loading}
                        size="sm"
                      >
                        {categoryImageUpload.loading
                          ? "Se încarcă..."
                          : "Încarcă imagine"}
                      </Button>
                      {categoryImageUpload.errors.length > 0 && (
                        <div className="text-sm text-red-600">
                          {categoryImageUpload.errors[0].message}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Card Image Upload Section */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Imagine Card Categorie
                  </label>
                  {categoryFormData.cardImage ? (
                    <div className="relative">
                      <img
                        src={categoryFormData.cardImage}
                        alt="Preview card"
                        className="w-32 h-32 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setCategoryFormData((f) => ({ ...f, cardImage: "" }));
                          categoryCardImageUpload.setFiles([]);
                          categoryCardImageUpload.setErrors([]);
                          setCardDropzoneKey((prev) => prev + 1);
                          lastCardUploadedResultsLengthRef.current = 0;
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Dropzone
                      {...categoryCardImageUpload}
                      key={`category-card-upload-${
                        editingCategory?.id || "new"
                      }-${cardDropzoneKey}`}
                    >
                      <DropzoneEmptyState />
                      <DropzoneContent />
                    </Dropzone>
                  )}
                  {categoryCardImageUpload.files.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        onClick={async () => {
                          await categoryCardImageUpload.onUpload();
                        }}
                        disabled={categoryCardImageUpload.loading}
                        size="sm"
                      >
                        {categoryCardImageUpload.loading
                          ? "Se încarcă..."
                          : "Încarcă imagine card"}
                      </Button>
                      {categoryCardImageUpload.errors.length > 0 && (
                        <div className="text-sm text-red-600">
                          {categoryCardImageUpload.errors[0].message}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelCategoryForm}
                  >
                    Anulează
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createCategory.isPending ||
                      updateCategory.isPending ||
                      categoryImageUpload.loading ||
                      categoryCardImageUpload.loading
                    }
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {createCategory.isPending || updateCategory.isPending
                      ? "Se salvează..."
                      : editingCategory
                      ? "Actualizează"
                      : "Salvează"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Category Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Caută categorii..."
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
          />
        </div>

        {/* Categories List */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">ID</TableHead>
                <TableHead className="font-semibold">Imagine</TableHead>
                <TableHead className="font-semibold">Nume</TableHead>
                <TableHead className="font-semibold">Slug</TableHead>
                <TableHead className="font-semibold">Data Creării</TableHead>
                <TableHead className="font-semibold">Subcategorii</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categoriesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="flex items-center justify-center text-muted-foreground">
                      Se încarcă categoriile...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-muted-foreground">
                      {categorySearch
                        ? "Nu există categorii care să se potrivească cu căutarea"
                        : "Nu există categorii"}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((category) => (
                  <>
                    <TableRow key={category.id} className="">
                      <TableCell className="font-medium">
                        #{category.id}
                      </TableCell>
                      <TableCell>
                        {category.imageUrl ? (
                          <img
                            src={category.imageUrl}
                            alt={category.name}
                            className="w-12 h-12 object-cover rounded-md border"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-md border bg-gray-100 flex items-center justify-center">
                            <Image className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-foreground">
                          {category.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{category.slug}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(category.createdAt).toLocaleDateString(
                          "ro-RO"
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {(() => {
                            const anyCat: any = category as any;
                            const subs = anyCat.subcategories;
                            return Array.isArray(subs) ? subs.length : 0;
                          })()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={() => handleCategoryEdit(category)}
                          >
                            <Edit className="w-4 h-4" />
                            Editează
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={() => toggleExpanded(category.id)}
                          >
                            Subcategorii
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCategoryDelete(category.id)}
                            disabled={
                              deleteCategory.isPending &&
                              deletingCategoryId === category.id
                            }
                          >
                            {deleteCategory.isPending &&
                            deletingCategoryId === category.id ? (
                              <>
                                <Spinner className="mr-2" /> Ștergere...
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" /> Șterge
                              </>
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedCategoryIds.has(category.id) && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <SubcategoriesPanel categoryId={category.id} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

function SubcategoriesPanel({ categoryId }: { categoryId: number }) {
  const queryClient = useQueryClient();
  const { data: items, isLoading } = useQuery<Subcategory[]>({
    queryKey: ["subcategories", categoryId],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/categories/${categoryId}/subcategories`
      );
      if (!res.ok) return [] as Subcategory[];
      const json = await res.json();
      if (Array.isArray(json)) return json as Subcategory[];
      if (json && Array.isArray(json.data)) return json.data as Subcategory[];
      if (json && Array.isArray(json.date)) return json.date as Subcategory[];
      return [] as Subcategory[];
    },
  });

  const [form, setForm] = React.useState<{
    name: string;
    slug: string;
    description: string;
  }>({ name: "", slug: "", description: "" });
  const [subSlugTouched, setSubSlugTouched] = React.useState(false);
  const [localOrder, setLocalOrder] = React.useState<Subcategory[]>([]);
  const [deletingSubId, setDeletingSubId] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (items) setLocalOrder(items);
  }, [items]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["subcategories", categoryId],
    });
  };

  const createSub = useMutation({
    mutationFn: async (payload: {
      name: string;
      slug: string;
      description?: string;
    }) => {
      const res = await fetchWithAuth(
        `/api/admin/categories/${categoryId}/subcategories`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...payload }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la crearea subcategoriei");
      }
      return res.json();
    },
    onSuccess: async () => {
      setForm({ name: "", slug: "", description: "" });
      await invalidate();
    },
  });

  const updateSub = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<Subcategory>;
    }) => {
      const res = await fetchWithAuth(`/api/admin/subcategories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(data.name !== undefined ? { name: data.name } : {}),
          ...(data.slug !== undefined ? { slug: data.slug } : {}),
          ...(data.description !== undefined
            ? { description: data.description }
            : {}),
          ...(data.displayOrder !== undefined
            ? { displayOrder: data.displayOrder }
            : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la actualizare");
      }
      return res.json();
    },
    onSuccess: invalidate,
  });

  const deleteSub = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetchWithAuth(`/api/admin/subcategories/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la ștergere");
      }
      return true;
    },
    onSuccess: invalidate,
    onSettled: () => {
      setDeletingSubId(null);
    },
  });

  const reorderSub = useMutation({
    mutationFn: async (orderIds: number[]) => {
      const res = await fetchWithAuth(
        `/api/admin/categories/${categoryId}/subcategories/reorder`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order: orderIds }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la reordonare");
      }
      return res.json();
    },
    onSuccess: invalidate,
  });

  const move = (from: number, to: number) => {
    setLocalOrder((prev) => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  return (
    <div className="rounded border p-3 bg-background/50">
      <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
        <Input
          placeholder="Nume subcategorie"
          value={form.name}
          onChange={(e) => {
            const next = e.target.value;
            setForm((f) => ({
              ...f,
              name: next,
              slug: subSlugTouched ? f.slug : slugify(next),
            }));
          }}
        />
        <Input
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => {
            setSubSlugTouched(true);
            setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
          }}
        />
        <Input
          placeholder="Descriere (opțional)"
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
        />
        <Button
          onClick={() => {
            if (!form.name.trim() || !form.slug.trim()) return;
            createSub.mutate({
              name: form.name.trim(),
              slug: form.slug.trim(),
              description: form.description || undefined,
            });
          }}
          disabled={createSub.isPending}
        >
          {createSub.isPending ? "Se adaugă..." : "Adaugă subcategorie"}
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Spinner className="w-4 h-4" /> Se încarcă subcategoriile...
        </div>
      ) : (localOrder || []).length === 0 ? (
        <div className="text-sm text-muted-foreground">
          Nu există subcategorii
        </div>
      ) : (
        <div className="space-y-2">
          {localOrder.map((s, idx) => (
            <div
              key={s.id}
              className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center"
            >
              {/* <div className="text-xs text-muted-foreground">#{s.id}</div> */}
              <Input
                defaultValue={s.name}
                placeholder="Nume subcategorie"
                onBlur={(e) =>
                  updateSub.mutate({ id: s.id, data: { name: e.target.value } })
                }
              />
              <Input
                defaultValue={s.slug}
                placeholder="Slug"
                onBlur={(e) =>
                  updateSub.mutate({
                    id: s.id,
                    data: { slug: slugify(e.target.value) },
                  })
                }
              />
              <Input
                defaultValue={s.description || ""}
                placeholder="Descriere (opțional)"
                onBlur={(e) =>
                  updateSub.mutate({
                    id: s.id,
                    data: { description: e.target.value },
                  })
                }
              />
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => idx > 0 && move(idx, idx - 1)}
                  disabled={idx === 0}
                  className="px-2"
                >
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    idx < localOrder.length - 1 && move(idx, idx + 1)
                  }
                  disabled={idx === localOrder.length - 1}
                  className="px-2"
                >
                  <ArrowDown className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDeletingSubId(s.id);
                    deleteSub.mutate(s.id);
                  }}
                  disabled={deleteSub.isPending && deletingSubId === s.id}
                >
                  {deleteSub.isPending && deletingSubId === s.id ? (
                    <>
                      <Spinner className="mr-2" /> Ștergere...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" /> Șterge
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <Button
              onClick={() => reorderSub.mutate(localOrder.map((x) => x.id))}
              disabled={reorderSub.isPending}
              className="mt-2"
            >
              {reorderSub.isPending ? "Se salvează..." : "Salvează ordinea"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// count is now derived from category.subcategories
